import { ActionContext } from "vuex/types";
import { General } from "@/utils/constants";

import {
  createGDocModel,
  getPublicCompleteFile,
  getPagedModelList,
  deleteProcessFile,
  orderProcessFile,
  getProcessesCompleteFile,
  setKnowledgeFile,
  getSignatureDocuments
} from "@/services/api/process/ApiProcess";

import {
  editProcessList,
  editDocumentTypeBatch,
  getDocumentCloneListRequest,
  getDocumentValidation,
  createDocument,
  createDocumentDOE,
  editDocumentDOE,
  getDocument,
  getDOEDocument,
  getProcessFiles,
  getListAttachment,
  setProcessFileNoEffect,
  setProcessToPublish,
  authorizeToPublish,
  getDispatchFiles,
  getJointFiles,
  documentCloneRequest,
  getAllowedExtensions,
  getProcessFilesFiltered,
  getFileSize,
  getFileCanGiveKnowledge,
  getFileValidators,
  getPageFiles,
  setProcessFileRevertNoEffect
} from "@/services/api/process/ApiFile";

import {
  createModel,
  deleteModel,
  getModelById,
  editModel,
  getAllModelList,
  getModelsListByType,
  getGdocDocumentAndModels,
  copyGDocDocument,
  cloneGDocModel
} from "@/services/api/process/ApiDocument";

import { getDocumentPrintVersion } from "@/services/api/process/ApiSignature";
import {
  ResponseType,
  ResponsePaged,
  ResponseGenericType,
  FullFilterPaged,
  IParamPaged,
  IGiveKnowledge
} from "@/store/types/types";
import {
  IFilterFilePaged,
  IFilePrinterVersion,
  IProcessFileComplete,
  IProcessFileNoEffect,
  IProcessFileId,
  IDocument,
  IEditFileType,
  IGdocFile,
  IFileTypeLocation,
  IFileExtension,
  IProcessFileFilter,
  IFilesPage,
  IObjectFile,
  StateFiles,
  IUpdateFiles,
  IGenericPaged,
  StateFilesCount,
  IFileSize,
  IFileValidators,
  IProcessFileRevertNoEffect
} from "@/store/types/file-type";
import {
  IDocumentModel,
  IDocumentDispatch,
  IProcessDocumentDOE,
  IDocumentDownload,
  IProcessDocumentType,
  IDocumentAuthorize,
  IProcessDocumentId,
  ICreateDocument,
  IProcessIdDocumentType,
  IDocumentTypeModels,
  ISetToPublishDocument,
  IEditGDoc,
  IDocumentModelsList,
  IEditDocumentDOE,
  IProcessDocumentTypeId,
  IModelEditable,
  IDocumentClone,
  ICloneModel
} from "@/store/types/document-type";
import { IGiveKnowledgeRequest } from "@/store/types/user-types";
import { IProcessId } from "@/store/types/process-type";
import { IRequiredSignature } from "@/store/types/signature-type";
import { showErrorMessage } from "@/utils/messageUtility";
import { RootState } from "@/store/types/root";

const state = {
  primaryFile: {} as Record<string, IProcessFileComplete>,
  processFiles: [] as IProcessFileComplete[],
  processFilesEditables: [] as IProcessFileComplete[],
  processFilesCount: 0,
  processFilesEditablesCount: 0,
  dispatchFiles: [] as IProcessFileComplete[],
  dispatchFilesEditables: [] as IProcessFileComplete[],
  dispatchFilesCount: 0,
  dispatchFilesEditablesCount: 0,
  jointFiles: [] as IProcessFileComplete[],
  jointFilesCount: 0,
  currentFile: {} as IFilePrinterVersion,
  currentDoeFile: {} as IProcessFileComplete,
  validDocument: {} as IDocument,
  gdocDocumentAndModels: {} as IDocumentTypeModels,
  gDocModel: {} as IGdocFile,
  allModels: [] as IDocumentModelsList[],
  processAttachmentFiles: [] as IProcessFileComplete[],
  processAttachmentFilesCount: 0,
  documentModelbyId: {} as ICreateDocument,
  documentModelsbyType: [] as IDocumentModelsList[],
  modeldocumentList: [] as IDocumentModel[],
  modeldocumentListCount: 0,
  editableFileExtensions: [] as IFileExtension[],
  notEditableFileExtensions: [] as IFileExtension[],
  publishList: [] as IFileExtension[],
  documentToUpdate: {} as IProcessFileFilter,
  processFilePages: [] as IFilesPage[]
};

type State = typeof state;

const mutations = {
  removePrimaryFile: (state: State, processId: string) => {
    delete state.primaryFile[processId];
  },

  setProcessFile: (state: State, data: ResponsePaged<IProcessFileComplete>) => {
    state.processFiles = data.results;
    state.processFilesCount = data.count;
    data.results.forEach(file => {
      if (file.primary) state.primaryFile[file.process_id] = file;
    });
  },

  removeFile: (
    state: State,
    { file, stateName }: { file: IProcessFileComplete; stateName: StateFiles }
  ) => {
    if (!state[stateName]) return;
    state[stateName] = state[stateName].filter(
      actualFile => actualFile.id !== file.id
    );
    const countState = (stateName + "Count") as StateFilesCount;
    state[countState]--;
  },

  checkAndRemovePrimaryFiles: (state: State, file: IProcessFileComplete) => {
    Object.entries(state.primaryFile).forEach(([processId, fileObj]) => {
      if (file.id === fileObj.id) delete state.primaryFile[processId];
    });
    state.primaryFile = { ...state.primaryFile };
  },

  dropProcessFile: (state: State, files_id: string[]) => {
    if (!files_id?.length) return;
    function removeFile(stateName: StateFiles) {
      if (!state[stateName]) return;
      state[stateName] = state[stateName].filter(
        actualFile => !files_id?.includes(actualFile.id)
      );
    }
    Object.entries(state.primaryFile).forEach(([processId, file]) => {
      if (files_id.includes(file.id)) delete state.primaryFile[processId];
    });
    state.primaryFile = { ...state.primaryFile };
    removeFile("jointFiles");
    removeFile("processAttachmentFiles");
    removeFile("dispatchFilesEditables");
    removeFile("dispatchFiles");
    removeFile("processFilesEditables");
    removeFile("processFiles");
    removeFile("dispatchFiles");
  },

  setProcessFileAttachment: (
    state: State,
    data: IGenericPaged<IProcessFileComplete>
  ) => {
    state.processAttachmentFiles = data.results;
    state.processAttachmentFilesCount = data.count;
  },

  setProcessFileEditable: (
    state: State,
    data: ResponsePaged<IProcessFileComplete>
  ) => {
    state.processFilesEditables = data.results;
    state.processFilesEditablesCount = data.count;
  },

  updateFilesAttachments: (
    state: State,
    {
      file,
      stateName
    }: {
      file: IProcessFileComplete;
      stateName: StateFiles;
    }
  ) => {
    if (!state[stateName]) return;
    state[stateName].push(file);
    const countState = (stateName + "Count") as StateFilesCount;
    state[countState]++;
  },

  dispatchFileSuccess: (
    state: State,
    data: ResponsePaged<IProcessFileComplete>
  ) => {
    state.dispatchFiles = data.results;
    state.dispatchFilesCount = data.count;
    data.results.forEach(file => {
      if (file.primary && file.dispatch_id)
        state.primaryFile[file.dispatch_id] = file;
    });
  },
  dispatchFileEditable: (
    state: State,
    data: ResponsePaged<IProcessFileComplete>
  ) => {
    state.dispatchFilesEditables = data.results;
    state.dispatchFilesEditablesCount = data.count;
  },

  setJointFiles: (state: State, data: IGenericPaged<IProcessFileComplete>) => {
    state.jointFiles = data.results || [];
    state.jointFilesCount = data.count;
  },

  updateJointFiles: (
    state: State,
    data: IGenericPaged<IProcessFileComplete>
  ) => {
    const newList = data.results.reduce(
      (acc: { [key: string]: IProcessFileComplete }, actual) => {
        acc[actual.id] = actual;
        return acc;
      },
      {}
    );
    state.jointFiles = state.jointFiles.map(oldFile => {
      return newList[oldFile.id] ?? oldFile;
    });
  },

  updateProcessList: (state: State, file: IProcessFileComplete) => {
    state.processFiles.forEach((oldFile, index) => {
      if (oldFile.id === file.id)
        state.processFiles[index] = { ...oldFile, ...file };
    });
    state.dispatchFiles.forEach((oldFile, index) => {
      if (oldFile.id === file.id)
        state.dispatchFiles[index] = { ...oldFile, ...file };
    });
    state.dispatchFiles.forEach((oldFile, index) => {
      if (oldFile.id === file.id) state.dispatchFiles[index] = { ...file };
    });
  },

  updateEditableProcessList: (state: State, file: IProcessFileComplete) => {
    state.processFilesEditables.forEach((oldFile, index) => {
      if (oldFile.id === file.id)
        state.processFilesEditables[index] = { ...oldFile, ...file };
    });
    state.dispatchFilesEditables.forEach((oldFile, index) => {
      if (oldFile.id === file.id)
        state.dispatchFilesEditables[index] = { ...oldFile, ...file };
    });
    state.dispatchFilesEditables.forEach((oldFile, index) => {
      if (oldFile.id === file.id)
        state.dispatchFilesEditables[index] = { ...file };
    });
  },

  setCurrentFile: (state: State, data: IFilePrinterVersion) => {
    state.currentFile = data;
  },

  setCurrentDOEFile: (state: State, data: IProcessFileComplete) => {
    state.currentDoeFile = data;
  },

  setCurrentFileValidated: (state: State, validated: boolean) => {
    state.currentFile.processFile.is_validated = validated;
  },

  setValidFile: (state: State, data: IDocument) => {
    state.validDocument = data;
  },

  setGDocModel: (state: State, data: IGdocFile) => {
    state.gDocModel = data;
  },

  setAllModels: (state: State, data: IDocumentModelsList[]) => {
    state.allModels = data;
  },

  setDocumentModelbyId: (state: State, data: ICreateDocument) => {
    state.documentModelbyId = data;
  },

  setDocumentModelsByType: (state: State, data: IDocumentModelsList[]) => {
    state.documentModelsbyType = data;
  },
  setCreateGdocDocumentAndModels: (state: State, data: IDocumentTypeModels) => {
    state.gdocDocumentAndModels = data;
  },

  setModelDocumentList: (state: State, data: ResponsePaged<IDocumentModel>) => {
    state.modeldocumentList = data.results;
    state.modeldocumentListCount = data.count;
  },

  setAllowedExtensionsList: (state: State, data: IFileExtension[]) => {
    state.editableFileExtensions = data.filter(
      fileExtension => fileExtension.editable === true
    );
    state.notEditableFileExtensions = data.filter(
      fileExtension => fileExtension.editable === false
    );
    state.publishList = data.filter(fileExtension => fileExtension.to_publish);
  },

  socketSetDocumentToUpdate: (state: State, document: IProcessFileFilter) => {
    state.documentToUpdate = document;
  },

  clearDocumentToUpdate: (state: State) => {
    state.documentToUpdate = {} as IProcessFileFilter;
  },

  setProcessFilePages: (state: State, listPages: IFilesPage[]) => {
    state.processFilePages = listPages;
  }
};

const actions = {
  postGiveKnowledgeFile: async (
    _: ActionContext<State, RootState>,
    {
      process_file_ids,
      process_id,
      user,
      LGPD,
      observation,
      is_external
    }: IGiveKnowledgeRequest
  ) => {
    const results = await setKnowledgeFile({
      process_file_ids,
      process_id,
      user,
      LGPD,
      observation,
      is_external
    });
    return { list: results.data, state: results.kind === "ok" };
  },

  validateDocument: async (
    { commit }: ActionContext<State, RootState>,
    hash: string
  ) => {
    const results: ResponseType<IDocument> = await getDocumentValidation(hash);

    if (results.kind === "ok") commit("setValidFile", results.data);
    else showErrorMessage(results.message);
  },

  processFilesFilteredRequest: async (
    { dispatch }: ActionContext<State, RootState>,
    filters: IProcessFileFilter
  ) => {
    const results: ResponseType<IProcessFileComplete[]> = await getProcessFilesFiltered(
      filters
    );

    if (results.kind === "ok") {
      if (filters.processId) {
        dispatch("updateProcessObjectFile", {
          list: results.data,
          filters
        });
      }
    } else showErrorMessage(results.message);
  },

  updateProcessObjectFile: async (
    { commit, dispatch }: ActionContext<State, RootState>,
    {
      list,
      filters
    }: { list: IProcessFileComplete[]; filters: IProcessFileFilter }
  ) => {
    if (filters.service !== "broadcast_local")
      dispatch("sendAllChannels", {
        action: "update_local_file",
        service: "broadcast_local",
        data: { list, filters: { ...filters, service: "broadcast_local" } },
        notSendAllSlaves: true
      });
    if (list?.length)
      dispatch("updateProcessFile", {
        list,
        isArchived: filters.isArchived,
        isRevertNoEffect: filters.isRevertNoEffect
      });
    else commit("dropProcessFile", filters.files_id);
  },

  processFileRequest: async (
    { commit }: ActionContext<State, RootState>,
    {
      processId,
      page = 1,
      search,
      paginator_count,
      order_by,
      only_count,
      editable = false,
      filters = {}
    }: IFilterFilePaged
  ) => {
    if (!processId) return;
    const results: ResponseType<ResponsePaged<
      IProcessFileComplete
    >> = await getProcessFiles({
      processId,
      editable,
      search,
      page,
      paginator_count,
      order_by,
      filters,
      only_count
    });

    if (results.kind === "ok") {
      const resultsData = !only_count ? results?.data?.results : [];
      const data = {
        ...results.data,
        results: resultsData
      };
      if (!editable) commit("setProcessFile", data);
      else commit("setProcessFileEditable", data);
    } else showErrorMessage(results.message);
  },

  getListAttachmentRequest: async (
    { commit }: ActionContext<State, RootState>,
    data: { processId: string; type: "archive" | "noeffect" } & IParamPaged
  ) => {
    const results: ResponseType<ResponsePaged<
      IProcessFileComplete
    >> = await getListAttachment(data);

    if (results.kind === "ok") {
      commit("setProcessFileAttachment", results.data);
    } else showErrorMessage(results.message);
  },

  setProcessFileNoEffect: async (
    _: ActionContext<State, RootState>,
    processFileNoEffect: IProcessFileNoEffect
  ) => {
    const results: ResponseGenericType = await setProcessFileNoEffect(
      processFileNoEffect
    );
    if (results.kind !== "ok") showErrorMessage(results.message);
  },

  setProcessFileRevertNoEffect: async (
    _: ActionContext<State, RootState>,
    processFileRevertNoEffect: IProcessFileRevertNoEffect
  ) => {
    const results: ResponseGenericType = await setProcessFileRevertNoEffect(
      processFileRevertNoEffect
    );
    if (results.kind !== "ok") showErrorMessage(results.message);
  },

  processToPublishRequest: async (
    { dispatch, commit }: ActionContext<State, RootState>,
    {
      toPublish,
      processId,
      processFileId,
      dispatchId,
      keepReference,
      nup
    }: ISetToPublishDocument
  ) => {
    const results: ResponseType<{
      process_file: string;
    }> = await setProcessToPublish({
      toPublish,
      processId,
      processFileId,
      dispatchId,
      keepReference
    });
    const success = results.kind == "ok";
    if (success) {
      const file_id = results.data?.process_file;
      if (file_id) {
        if (toPublish) {
          dispatch("processFilesFilteredRequest", {
            processId,
            dispatch_id: dispatchId,
            files_id: [file_id]
          });
          return file_id;
        } else {
          commit("deleteProcessFile", {
            file: {
              id: file_id,
              dispatch_id: dispatchId,
              nup,
              editable: false
            },
            isArchived: false
          });
        }
      }
    } else showErrorMessage(results.message);
    return success;
  },

  authorizeToPublishRequest: async (
    _: ActionContext<State, RootState>,
    { authorize, processFileId, processId }: IDocumentAuthorize
  ) => {
    const results: ResponseGenericType = await authorizeToPublish({
      authorize,
      processFileId,
      processId
    });
    if (results.kind !== "ok") showErrorMessage(results.message);
  },

  dispatchFileRequest: async (
    { commit }: ActionContext<State, RootState>,
    {
      dispatchId,
      page = 1,
      paginator_count = 10,
      editable = false
    }: IDocumentDispatch
  ) => {
    const results: ResponseType<ResponsePaged<
      IProcessFileComplete
    >> = await getDispatchFiles({
      dispatchId,
      page,
      paginator_count,
      editable
    });

    if (results.kind === "ok") {
      if (!editable) commit("dispatchFileSuccess", results.data);
      else commit("dispatchFileEditable", results.data);
    } else showErrorMessage(results.message);
  },

  printableVersionRequest: async (
    { commit }: ActionContext<State, RootState>,
    { process_file_id, process_id = "" }: IProcessFileId
  ) => {
    const results: ResponseType<IProcessFileComplete> = await getDocumentPrintVersion(
      { process_file_id, process_id }
    );
    const status = results.kind === "ok";

    if (results.kind === "Not Found")
      results.message = General.PROCESS_NOT_FOUND;

    if (status) commit("setCurrentFile", results.data);
    else showErrorMessage(results.message);

    return status;
  },

  editProcessFile: async (
    { commit }: ActionContext<State, RootState>,
    { gdoc, editable = false }: IEditGDoc
  ) => {
    const results: ResponseGenericType = await editProcessList(gdoc);

    if (results.kind === "ok") {
      if (!editable) commit("updateProcessList", gdoc);
      else commit("updateEditableProcessList", gdoc);
    } else showErrorMessage(results.message);

    return results;
  },

  editDocumentTypeBatch: async (
    _: ActionContext<State, RootState>,
    { process_id, document_type_id, process_file_ids }: IEditFileType
  ) => {
    const results: ResponseGenericType = await editDocumentTypeBatch({
      process_id,
      document_type_id,
      process_file_ids
    });

    if (results.kind !== "ok") {
      showErrorMessage(results.message);
    }

    return results;
  },

  getJointFile: async (
    { commit }: ActionContext<State, RootState>,
    data: {
      jointId: string;
      only_count: boolean;
      files_id?: string;
    } & IParamPaged
  ) => {
    const results: ResponseType<IProcessFileComplete[]> = await getJointFiles(
      data
    );

    if (results.kind === "ok") {
      commit(
        data.files_id ? "updateJointFiles" : "setJointFiles",
        results.data
      );
    } else showErrorMessage(results.message);
  },

  createGDocModelRequest: async (
    { commit }: ActionContext<State, RootState>,
    documentTypeId: string
  ) => {
    const results: ResponseType<IGdocFile> = await createGDocModel(
      documentTypeId
    );

    if (results.kind === "ok") commit("setGDocModel", results.data);
    else showErrorMessage(results.message);
  },

  copyGdocDocumentRequest: async (
    _: ActionContext<State, RootState>,
    { processId, documentTypeId, documentModelId }: IProcessDocumentId
  ) => {
    const results: ResponseType<IGdocFile> = await copyGDocDocument({
      processId,
      documentTypeId,
      documentModelId
    });

    if (results.kind === "ok") return results;
    else showErrorMessage(results.message);
  },

  createModelRequest: async (
    _: ActionContext<State, RootState>,
    { id, name = "", documentType, all_parts, parts, gdocsId }: ICreateDocument
  ) => {
    const results: ResponseType<ICreateDocument> = await createModel({
      id,
      name,
      documentType,
      all_parts,
      parts,
      gdocsId
    });

    if (results.kind !== "ok") showErrorMessage(results.message);

    return results;
  },

  editModelRequest: async (
    _: ActionContext<State, RootState>,
    { id, name = "", documentType, all_parts, parts, gdocsId }: ICreateDocument
  ) => {
    const results: ResponseType<ICreateDocument> = await editModel({
      id,
      name,
      documentType,
      all_parts,
      parts,
      gdocsId
    });

    if (results.kind !== "ok") showErrorMessage(results.message);

    return results;
  },

  deleteModelRequest: async (
    _: ActionContext<State, RootState>,
    modelId: string
  ) => {
    const results: ResponseGenericType = await deleteModel(modelId);

    if (results.kind !== "ok") showErrorMessage(results.message);

    return results;
  },

  cloneGDocModelRequest: async (
    _: ActionContext<State, RootState>,
    modelId: string
  ) => {
    const results: ResponseType<ICloneModel> = await cloneGDocModel(modelId);

    if (results.kind === "ok" && results.data) return results.data.gdocs_id;
    showErrorMessage(results.message);
  },

  getModelbyIdRequest: async (
    { commit }: ActionContext<State, RootState>,
    { modelId, edit = true }: IModelEditable
  ) => {
    const results: ResponseType<ICreateDocument> = await getModelById(
      modelId,
      edit
    );
    const status = results.kind === "ok";

    if (status) commit("setDocumentModelbyId", results.data);
    else showErrorMessage(results.message);

    return status;
  },

  getAllModelList: async ({ commit }: ActionContext<State, RootState>) => {
    const results: ResponseType<IDocumentModelsList[]> = await getAllModelList();

    if (results.kind === "ok") commit("setAllModels", results.data);
    else showErrorMessage(results.message);
  },

  getModelsListByTypeRequest: async (
    { commit }: ActionContext<State, RootState>,
    { documentTypeId, isEditable = false }: IProcessDocumentTypeId
  ) => {
    const results: ResponseType<IDocumentModelsList[]> = await getModelsListByType(
      { documentTypeId, isEditable }
    );

    if (results.kind === "ok") commit("setDocumentModelsByType", results.data);
    else showErrorMessage(results.message);
  },

  requestModel: async (
    { commit }: ActionContext<State, RootState>,
    {
      page,
      paginator_count,
      search,
      document_type,
      partId,
      capacityId
    }: FullFilterPaged
  ) => {
    const results: ResponseType<ResponsePaged<
      IDocumentModel
    >> = await getPagedModelList({
      page,
      paginator_count,
      search,
      document_type,
      partId,
      capacityId
    });

    if (results.kind === "ok") commit("setModelDocumentList", results.data);
    else showErrorMessage(results.message);
  },

  getCreateGdocDocumentAndModels: async (
    { commit }: ActionContext<State, RootState>,
    { processId, documentType }: IProcessDocumentType
  ) => {
    const results: ResponseType<IDocumentTypeModels> = await getGdocDocumentAndModels(
      {
        processId,
        documentType
      }
    );

    if (results.kind === "ok") {
      commit("setCreateGdocDocumentAndModels", results.data);
      commit("setDocumentModelsByType", results.data?.document_models);
    } else {
      showErrorMessage(results.message);
    }
    return results.kind === "ok";
  },

  createDocumentFile: async (
    _: ActionContext<State, RootState>,
    file: IProcessFileComplete
  ) => {
    const results: ResponseType<IProcessFileComplete> = await createDocument(
      file
    );
    if (results.kind !== "ok") showErrorMessage(results.message);
    return results;
  },

  createDocumentDOEFile: async (
    _: ActionContext<State, RootState>,
    fileDOE: IProcessDocumentDOE
  ) => {
    const results: ResponseGenericType = await createDocumentDOE(fileDOE);
    const status = results.kind === "ok";
    if (results.kind !== "ok") showErrorMessage(results.message);
    return status;
  },

  editDocumentDOEFile: async (
    _: ActionContext<State, RootState>,
    { processFileId, fileDOE }: IEditDocumentDOE
  ) => {
    const results: ResponseGenericType = await editDocumentDOE(
      processFileId,
      fileDOE
    );
    const status = results.kind === "ok";

    if (results.kind !== "ok") showErrorMessage(results.message);

    return status;
  },

  getDocumentFile: async (
    { commit }: ActionContext<State, RootState>,
    fileId: string
  ) => {
    const results: ResponseType<IFilePrinterVersion> = await getDocument(
      fileId
    );

    if (results.kind === "ok") commit("setCurrentFile", results.data);
    else showErrorMessage(results.message);
  },

  getDOEDocumentFile: async (
    { commit }: ActionContext<State, RootState>,
    fileId: string
  ) => {
    const results: ResponseType<IProcessFileComplete> = await getDOEDocument(
      fileId
    );

    if (results.kind === "ok") commit("setCurrentDOEFile", results.data);
    else showErrorMessage(results.message);
  },

  orderProcessFiles: async (
    { commit }: ActionContext<State, RootState>,
    files: IProcessFileComplete[]
  ) => {
    const results: ResponseType<ResponsePaged<
      IProcessFileComplete
    >> = await orderProcessFile(files);

    if (results.kind === "ok") {
      commit("setProcessFile", results.data);
      if (
        results.data?.results.some(
          (file: IProcessFileComplete) => file.document_join_id
        )
      )
        commit("setJointFiles", {
          ...results.data,
          results: results.data?.results.filter(
            (file: IProcessFileComplete) => file.document_join_id
          )
        });
    } else showErrorMessage(results.message);
  },

  deleteProcessFileAction: async (
    { commit }: ActionContext<State, RootState>,
    {
      file,
      isArchived,
      fileSize,
      isRevertNoEffect
    }: {
      file: IProcessFileComplete;
      isArchived: boolean;
      fileSize: number;
      isRevertNoEffect: boolean;
    }
  ) => {
    commit("checkAndRemovePrimaryFiles", file);

    let stateName = "";
    if (file.dispatch_id) {
      if (file.editable) stateName = "dispatchFilesEditables";
      else stateName = "dispatchFiles";
    } else if (file.document_join_id) stateName = "jointFiles";
    else if (isArchived || isRevertNoEffect)
      stateName = "processAttachmentFiles";
    else if (file.process_id) {
      if (file.editable) stateName = "processFilesEditables";
      else {
        stateName = "processFiles";
        if (!file.primary) commit("addProcessFileSize", -fileSize);
      }
    }

    commit("removeFile", { file, stateName });
  },

  deleteProcessFileRequest: async (
    { dispatch }: ActionContext<State, RootState>,
    {
      file,
      isArchived = false,
      isRevertNoEffect = false
    }: {
      file: IProcessFileComplete;
      isArchived?: boolean;
      isRevertNoEffect?: boolean;
    }
  ) => {
    let fileSize = 0;
    if (!file.primary) {
      fileSize = file.filesize;
      if (!fileSize) fileSize = await dispatch("getFileSizeRequest", file.id);
    }

    const results: ResponseType<string[]> = await deleteProcessFile({
      processFileId: file.id,
      isArchive: isArchived,
      isRevertNoEffect: isRevertNoEffect
    });

    if (results.kind === "ok") {
      dispatch("deleteProcessFileAction", {
        file,
        isArchived,
        fileSize,
        isRevertNoEffect
      });
      if (results.data?.length) {
        dispatch("processFilesFilteredRequest", {
          processId: file.process_id,
          dispatch_id: file.dispatch_id,
          files_id: results.data
        });
      }
    } else showErrorMessage(results.message);
    return results.kind === "ok";
  },

  getFileSizeRequest: async (
    _: ActionContext<State, RootState>,
    processFileId: string
  ) => {
    const results: ResponseType<IFileSize> = await getFileSize(processFileId);
    if (results.kind === "ok" && results.data) return results.data.file_size;
    else showErrorMessage(results.message);
    return 0;
  },

  getFileCanGiveKnowledgeRequest: async (
    _: ActionContext<State, RootState>,
    { processId, processFileId }: { processId: string; processFileId: string }
  ) => {
    const results: ResponseType<IGiveKnowledge> = await getFileCanGiveKnowledge(
      { processId, processFileId }
    );
    if (results.kind === "ok" && results.data)
      return results.data.can_give_knowledge;
    else showErrorMessage(results.message);
    return false;
  },

  getFileValidatorsRequest: async (
    _: ActionContext<State, RootState>,
    processFileId: string
  ) => {
    const results: ResponseType<IFileValidators[]> = await getFileValidators(
      processFileId
    );
    if (results.kind === "ok" && results.data) return results.data;
    else showErrorMessage(results.message);
    return false;
  },

  openPartialPDFStream: (
    _: ActionContext<State, RootState>,
    fileURL?: string
  ) => {
    if (fileURL) {
      window.open(fileURL, "_blank", "noopener");
    }
  },

  getCompleteProcessFileStream: async (
    _: ActionContext<State, RootState>,
    processId: string
  ) => {
    window.open(`/processo/${processId}`, "_blank", "noopener");
  },

  getProcessesCompleteFile: async (
    { dispatch }: ActionContext<State, RootState>,
    processIdList: IProcessId[]
  ) => {
    const results: ResponseType<IDocumentDownload[]> = await getProcessesCompleteFile(
      processIdList
    );

    if (results.kind === "ok")
      results.data?.forEach(({ urlFile }) =>
        dispatch("openPartialPDFStream", urlFile)
      );
    else showErrorMessage(results.message);
    return results.kind === "ok";
  },

  getSignatureDocuments: async (
    { dispatch }: ActionContext<State, RootState>,
    processFileIdList: string[]
  ) => {
    const results: ResponseType<IDocumentDownload[]> = await getSignatureDocuments(
      processFileIdList
    );

    if (results.kind === "ok")
      results.data?.forEach(({ urlFile }) =>
        dispatch("openPartialPDFStream", urlFile)
      );
    else showErrorMessage(results.message);
    return results.kind === "ok";
  },

  getCompletePublicFile: async (
    _: ActionContext<State, RootState>,
    processId: string
  ) => {
    const results: ResponseType<IDocumentDownload> = await getPublicCompleteFile(
      processId
    );

    if (results.kind === "ok") return results.data;
    else showErrorMessage(results.message);

    return null;
  },
  getDocumentCloneListRequest: async (
    _: ActionContext<State, RootState>,
    { processId, documentType }: IProcessIdDocumentType
  ) => {
    const results: ResponseType<IGdocFile[]> = await getDocumentCloneListRequest(
      processId,
      documentType
    );

    if (results.kind === "ok") {
      return results.data;
    }
    showErrorMessage(results.message);
  },
  documentCloneRequest: async (
    _: ActionContext<State, RootState>,
    { processFileId, data }: IDocumentClone
  ) => {
    const results: ResponseType<IGdocFile> = await documentCloneRequest({
      processFileId,
      data
    });
    if (results.kind === "ok") {
      return results.data;
    }
    showErrorMessage(results.message);
  },
  getAllowedExtensions: async ({ commit }: ActionContext<State, RootState>) => {
    const results: ResponseType<IFileExtension[]> = await getAllowedExtensions();
    if (results.kind === "ok") {
      commit("setAllowedExtensionsList", results.data);
    }
    showErrorMessage(results.message);
  },

  socketUpdateSignature: (
    { commit, state }: ActionContext<State, RootState>,
    data: IFilePrinterVersion
  ) => {
    [
      ...state.processFiles,
      ...state.dispatchFiles,
      ...state.jointFiles,
      state.currentFile
    ].forEach((file: IProcessFileComplete | IFilePrinterVersion) => {
      if (file.id === data.id) {
        file.sign_history = data.sign_history;
        file.can_tramit = data.can_tramit;
        file.status = data.status;
        file.GiveKnowledgeHistory = data.give_knowledge_history;
        file.SignatureFileHistory = data.sign_history;
        commit("socketSetDocumentToUpdate", {
          files_id: [file.id],
          processId: file.process_id,
          dispatch_id: file.dispatch_id
        });
      }
    });
  },

  updateProcessFile: (
    { getters, state }: ActionContext<State, RootState>,
    { list, isArchived, isRevertNoEffect }: IUpdateFiles
  ) => {
    if (!list.length) return;
    const mapFile = list.reduce(
      (acc: IObjectFile, actualFile: IProcessFileComplete) => {
        acc[actualFile.id] = actualFile;
        if (actualFile.primary)
          state.primaryFile[
            actualFile.dispatch_id || actualFile.process_id
          ] = actualFile;
        return acc;
      },
      {}
    );
    const notFound = Object.keys(mapFile);
    function checkAndReturn(file: IProcessFileComplete) {
      if (mapFile[file.id]) {
        notFound.splice(notFound.indexOf(file.id), 1);
        return mapFile[file.id];
      } else return file;
    }
    function updateOrAdd(stateName: StateFiles) {
      if (!state[stateName]) return;
      state[stateName] = state[stateName].map(checkAndReturn);
      notFound.forEach(file_id => state[stateName].push(mapFile[file_id]));
    }
    if (
      list[0].document_join_id &&
      getters.getCurrentJoint.id === list[0].document_join_id
    )
      updateOrAdd("jointFiles");
    else if (isArchived || isRevertNoEffect)
      updateOrAdd("processAttachmentFiles");
    else if (
      list[0].dispatch_id &&
      getters.getDispatch.id === list[0].dispatch_id &&
      list[0].editable
    )
      updateOrAdd("dispatchFilesEditables");
    else if (
      list[0].dispatch_id &&
      getters.getDispatch.id === list[0].dispatch_id &&
      !list[0].editable
    )
      updateOrAdd("dispatchFiles");
    else if (list[0].editable) updateOrAdd("processFilesEditables");
    else updateOrAdd("processFiles");
  },

  processFilePageRequest: async (
    { commit }: ActionContext<State, RootState>,
    processId: string
  ) => {
    const results: ResponseType<IFilesPage[]> = await getPageFiles(processId);
    if (results.kind === "ok") {
      commit("setProcessFilePages", results.data);
    } else showErrorMessage(results.message);
  }
};

interface Getters {
  getModifiedProcessFiles: IProcessFileComplete[];
  getProcessFilesEditable: IProcessFileComplete[];
  getProcessFiles: IProcessFileComplete[];
  getModifiedDispatchFiles: IProcessFileComplete[];
  getModifiedDispatchFilesEditable: IProcessFileComplete[];
  getModifiedJointFiles: IProcessFileComplete[];
  getListAttachment: IProcessFileComplete[];
  returnDocumentByType: (
    _filterType: IFileTypeLocation
  ) => IProcessFileComplete[] | undefined;
  countDocumentType: (_filterType: IFileTypeLocation) => number | undefined;
  getFileTypeById: (_filterType: string) => IProcessFileComplete;
  getSignatureHistoryType: (
    _file: IProcessFileComplete,
    _type: string
  ) => IRequiredSignature[];
}

const getters = {
  getProcessFiles: (state: State) => state.processFiles,
  getProcessFilesCount: (state: State) => state.processFilesCount,
  getDispatchFilesCount: (state: State) => state.dispatchFilesCount,
  getDispatchFilesEditablesCount: (state: State) =>
    state.dispatchFilesEditablesCount,
  getProcessFilesEditable: (state: State) => state.processFilesEditables,
  getProcessFilesEditableCount: (state: State) =>
    state.processFilesEditablesCount,

  returnDocumentByType: (_: State, getters: Getters) => (
    _filterType: IFileTypeLocation
  ) =>
    ({
      process: [
        ...getters.getModifiedProcessFiles,
        ...getters.getProcessFilesEditable
      ],
      dispatch: [
        ...getters.getModifiedDispatchFiles,
        ...getters.getModifiedDispatchFilesEditable
      ],
      join: getters.getModifiedJointFiles,
      archived: getters.getListAttachment,
      restoreEffect: getters.getListAttachment
    }[_filterType]),

  countDocumentType: (_: State, getters: Getters) => (
    _filterType: IFileTypeLocation
  ) => getters.returnDocumentByType(_filterType)?.length,

  getModifiedProcessFiles: (state: State, getters: Getters) => {
    const modifiedFiles: IProcessFileComplete[] = [];
    state.processFiles.forEach(file => {
      const type = file.document_type
        ? getters.getFileTypeById(file.document_type).type
        : "";
      if (!file.primary) modifiedFiles.push({ ...file, type });
    });
    return modifiedFiles;
  },

  checkDocStatus: (state: State) => (acceptState: string[]) =>
    state.processFiles.some(doc => !acceptState.includes(doc.status)),

  getModifiedProcessFilesEditable: (state: State, getters: Getters) => {
    const modifiedFiles: IProcessFileComplete[] = [];
    state.processFilesEditables.forEach(file => {
      const type = file.document_type
        ? getters.getFileTypeById(file.document_type).type
        : "";
      if (!file.primary) modifiedFiles.push({ ...file, type });
    });
    return modifiedFiles;
  },

  getProcessInitialFile: (state: State) => (processId: string) =>
    state.processFiles.find(
      file => file.process_id === processId && file.primary,
      null
    ) || state.primaryFile[processId],

  getDispatchFiles: (state: State) => state.dispatchFiles,

  getDispatchFilesEditable: (state: State) => state.dispatchFilesEditables,

  getModifiedJointFiles: (state: State, getters: Getters) => {
    const modifiedFiles: IProcessFileComplete[] = [];
    state.jointFiles.forEach(file => {
      const type = file.document_type
        ? getters.getFileTypeById(file.document_type).type
        : "";
      if (!file.primary) modifiedFiles.push({ ...file, type });
    });
    return modifiedFiles;
  },

  getModifiedDispatchFiles: (state: State, getters: Getters) => {
    const modifiedFiles: IProcessFileComplete[] = [];
    state.dispatchFiles.forEach(file => {
      const type = file.document_type_id
        ? getters.getFileTypeById(file.document_type_id).type
        : "";
      if (!file.primary) modifiedFiles.push({ ...file, type });
    });
    return modifiedFiles;
  },

  getModifiedDispatchFilesEditable: (state: State, getters: Getters) => {
    const modifiedFiles: IProcessFileComplete[] = [];
    state.dispatchFilesEditables.forEach(file => {
      const type = file.document_type_id
        ? getters.getFileTypeById(file.document_type_id).type
        : "";
      if (!file.primary) modifiedFiles.push({ ...file, type });
    });
    return modifiedFiles;
  },

  getDispatchInitialFile: (state: State) => (dispatch_id: string) =>
    state.dispatchFiles.find(
      file => file.dispatch_id === dispatch_id && file.primary,
      null
    ) || state.primaryFile[dispatch_id],

  getCurrentFile: (state: State) => state.currentFile,

  getCurrentDoeFile: (state: State) => state.currentDoeFile,

  getListAttachment: (state: State) => state.processAttachmentFiles,

  getListAttachmentCount: (state: State) => state.processAttachmentFilesCount,

  getGdocDocumentAndModels: (state: State) => state.gdocDocumentAndModels,

  getGDocModel: (state: State) => state.gDocModel,

  getAllModels: (state: State) => state.allModels,

  getModel: (state: State) => state.documentModelbyId,

  getModelsByTypeFiltered: (state: State) => (filter = "") => {
    if (filter === "") return state.documentModelsbyType;
    return state.documentModelsbyType.filter(
      model =>
        model.name.toLowerCase().includes(filter.toLowerCase()) ||
        model.id.toString() === filter
    );
  },
  getValidDocument: (state: State) => state.validDocument,

  getJointFiles: (state: State) => state.jointFiles,
  getJointFilesCount: (state: State) => state.jointFilesCount,

  getModelDocumentPaged: (state: State) => ({
    list: state.modeldocumentList,
    count: state.modeldocumentListCount
  }),

  getFileExtensionList: (state: State) => ({
    editable: state.editableFileExtensions,
    notEditable: state.notEditableFileExtensions
  }),

  getPublishExtensionList: (state: State) => state.publishList,

  getSignatureHistoryType: (_: State) => (
    file: IProcessFileComplete,
    type: string
  ) => {
    const history_distinct = file.sign_history?.filter(
      sub => !sub.only_historical
    );
    return (
      history_distinct?.filter(signature => signature.status === type) ?? []
    );
  },

  getProcessSignatureHistoryType: (_: State, getters: Getters) => (
    fileList: IProcessFileComplete[],
    type: string
  ) => {
    return fileList
      ?.filter(file => file)
      .reduce(
        (count: number, file: IProcessFileComplete) =>
          count + getters.getSignatureHistoryType(file, type)?.length,
        0
      );
  },

  getDraftFilesSize: (state: State) => (stateName: StateFiles) => {
    if (!state[stateName]) return 0;
    return state[stateName].reduce((acc, actual) => {
      if (!actual.primary) return actual.filesize + acc;
      else return acc;
    }, 0);
  },

  socketGetDocumentToUpdate: (state: State) => state.documentToUpdate,

  getProcessFilePages: (state: State) => state.processFilePages
};

export default {
  state,
  mutations,
  actions,
  getters
};
